import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, Trash2, Copy, Check, StickyNote } from 'lucide-react';
import useShoppingList from '../hooks/useShoppingList';

const NOTES_KEY = 'gw_shopping_notes';

export const ShoppingListDrawer = () => {
  const { items, toggleBought, removeItem, clearAll, count } = useShoppingList();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notes, setNotes] = useState(() => localStorage.getItem(NOTES_KEY) || '');

  const updateNotes = (val) => {
    setNotes(val);
    localStorage.setItem(NOTES_KEY, val);
  };

  // Group items by recipe, preserving insertion order
  const groups = [];
  const groupIndex = {};
  items.forEach((it) => {
    if (groupIndex[it.recipeId] === undefined) {
      groupIndex[it.recipeId] = groups.length;
      groups.push({ recipeId: it.recipeId, title: it.recipeTitle, items: [] });
    }
    groups[groupIndex[it.recipeId]].items.push(it);
  });

  const copyList = async () => {
    const listText = groups
      .map((g) => `${g.title}\n` + g.items.map((i) => `  - ${i.text}`).join('\n'))
      .join('\n\n');
    const text = notes.trim() ? `${listText}\n\nNotes:\n${notes.trim()}` : listText;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="shopping-list-fab"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-forest text-cream px-5 py-3 rounded-full shadow-lg hover:bg-forest-dark transition-colors"
        aria-label="Open shopping list"
      >
        <ShoppingCart size={20} />
        <span className="font-body font-medium text-sm hidden sm:inline">Shopping List</span>
        {count > 0 && (
          <span
            data-testid="shopping-list-count"
            className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-terracotta text-white text-xs font-body font-semibold flex items-center justify-center"
          >
            {count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-forest/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="shopping-list-overlay"
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute top-0 right-0 h-full w-full max-w-md bg-cream flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="shopping-list-panel"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200 bg-white">
                <div className="flex items-center gap-3">
                  <ShoppingCart size={22} className="text-terracotta" />
                  <div>
                    <h3 className="font-heading text-xl text-forest leading-none">Shopping List</h3>
                    <p className="text-xs font-body text-stone-500 mt-1">{count} item{count === 1 ? '' : 's'}</p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="shopping-list-close"
                  className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-forest transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5">
                {count === 0 ? (
                  <div className="text-center py-16">
                    <ShoppingCart size={40} className="text-stone-300 mx-auto mb-3" />
                    <p className="font-body text-stone-500">Your list is empty.</p>
                    <p className="font-body text-stone-400 text-sm mt-1">
                      Open any recipe and tick the ingredients you need to buy.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {groups.map((g) => (
                      <div key={g.recipeId} data-testid={`shopping-group-${g.recipeId}`}>
                        <h4 className="font-heading text-base text-forest mb-2">{g.title}</h4>
                        <ul className="space-y-1.5">
                          {g.items.map((it) => (
                            <li key={it.key} className="flex items-center gap-3 group">
                              <label className="flex items-center gap-3 flex-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!it.bought}
                                  onChange={() => toggleBought(it.key)}
                                  className="w-4 h-4 rounded border-stone-300 accent-emerald-600 cursor-pointer flex-shrink-0"
                                  data-testid={`shopping-bought-${it.key}`}
                                />
                                <span
                                  className={`font-body text-sm ${
                                    it.bought ? 'line-through text-stone-400' : 'text-stone-700'
                                  }`}
                                >
                                  {it.text}
                                </span>
                              </label>
                              <button
                                onClick={() => removeItem(it.key)}
                                className="text-stone-300 hover:text-red-500 transition-colors flex-shrink-0"
                                data-testid={`shopping-remove-${it.key}`}
                                aria-label="Remove item"
                              >
                                <X size={16} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quick notes */}
                <div className="mt-6 pt-5 border-t border-stone-200">
                  <label className="flex items-center gap-2 mb-2 text-forest">
                    <StickyNote size={16} className="text-terracotta" />
                    <span className="font-heading text-base">Notes</span>
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => updateNotes(e.target.value)}
                    placeholder="Jot anything down — e.g. buy extra beets, check pantry for olive oil…"
                    rows={4}
                    className="w-full font-body text-sm text-stone-700 bg-white border border-stone-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-terracotta resize-none placeholder:text-stone-400"
                    data-testid="shopping-list-notes"
                  />
                </div>
              </div>

              {/* Footer actions */}
              {count > 0 && (
                <div className="px-6 py-4 border-t border-stone-200 bg-white flex items-center gap-3">
                  <button
                    onClick={copyList}
                    data-testid="shopping-list-copy"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-forest text-cream px-4 py-2.5 rounded-full font-body font-medium text-sm hover:bg-forest-dark transition-colors"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy list'}
                  </button>
                  <button
                    onClick={clearAll}
                    data-testid="shopping-list-clear"
                    className="inline-flex items-center justify-center gap-2 text-stone-500 hover:text-red-600 px-4 py-2.5 rounded-full font-body text-sm transition-colors"
                  >
                    <Trash2 size={16} /> Clear all
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShoppingListDrawer;
